// Sentry/GlitchTip FE — bắt lỗi browser khách (uncaught error + promise rejection +
// React render crash qua ErrorBoundary ở main.jsx). Ingest qua hub.cnvwork.com/monitor
// (CÙNG ORIGIN với admin → không CORS, ad-block không chặn vì là domain nhà mình).
//
// DSN = build-time `VITE_SENTRY_DSN` (DSN public, nhúng browser là chuẩn). Trống → no-op (dev).
// KHÔNG performance/replay (tránh gửi DOM/PII). PII scrub ở beforeSend.

import * as Sentry from '@sentry/react';
import { setErrorReporter } from '@cnv/ui/lib/errorReporter';

const DSN = import.meta.env.VITE_SENTRY_DSN || '';

// Lỗi tải chunk = deploy rsync --delete xoá chunk-hash cũ, tab đang mở đòi file đã mất.
// Cùng danh sách với `isChunkLoadError` của @cnv/ui/utils/lazyWithReload (chỗ đó so trên
// Error, chỗ này so trên event đã dựng xong nên phải viết lại dạng chuỗi).
const CHUNK_LOAD_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
];

function isChunkLoadEvent(event) {
  const values = event?.exception?.values || [];
  const text = `${event?.message || ''} ${values.map((v) => `${v?.type || ''}: ${v?.value || ''}`).join(' ')}`;
  return CHUNK_LOAD_MESSAGES.some((m) => text.toLowerCase().includes(m.toLowerCase()));
}

if (DSN) {
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE === 'production' ? 'production' : 'development',
    release: import.meta.env.VITE_GIT_SHA || undefined,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [],
    // Bỏ noise vô hại (network/cancel/resizeobserver) → tránh ngập dashboard.
    //
    // ⚠️ CHUNK-LOAD KHÔNG CÒN NẰM Ở ĐÂY (đổi 18/08/2026 — đọc trước khi định thêm lại).
    // Lý do cũ ("transient, lazyWithReload tự reload 1 lần là khỏi") chỉ đúng với lần fail
    // ĐẦU. Khi lỗi lọt tới ErrorBoundary thì ngân sách reload đã cạn mà chunk vẫn 404 —
    // hết transient, và người dùng đang NHÌN THẤY hộp lỗi. Đúng cảnh 18/08: báo cáo CRM
    // hỏng cả buổi, GlitchTip trống trơn, phải SSH + đọc DB mới lần ra. `ignoreErrors` lọc
    // ở InboundFilters (CHẠY TRƯỚC beforeSend, event bị bỏ không bao giờ tới beforeSend)
    // nên không cách nào mở ngoại lệ từ đó → chuyển hẳn xuống beforeSend bên dưới.
    ignoreErrors: [
      'Network Error', 'Request aborted', 'AbortError', 'canceled',
      'ResizeObserver loop limit exceeded', 'ResizeObserver loop completed',
      'Non-Error promise rejection captured', 'Load failed',
      // 'Failed to fetch' = biến thể Chrome của lỗi network-layer khi fetch() fail (offline,
      // DNS, connection refused, bị extension/adblock chặn). Anh em với 'Network Error' (axios)
      // + 'Load failed' (Safari). Nguồn duy nhất ném lỗi này là fetch() thô ở background:
      // WebVersionWatcher poll /index.html + Sentry transport — đều ĐÃ try/catch, benign,
      // transient, KHÔNG actionable. API thật đi qua axios (giữ status-code error visible).
      // (GlitchTip issue 9 — throw từ chrome-extension wrap window.fetch)
      //
      // ⚠️ PHẢI LÀ REGEX NEO 2 ĐẦU, ĐỪNG ĐỔI VỀ CHUỖI. `ignoreErrors` dạng chuỗi khớp kiểu
      // SUBSTRING, mà "Failed to fetch dynamically imported module: /assets/x.js" CHỨA
      // "Failed to fetch" ⇒ để dạng chuỗi là nuốt luôn MỌI lỗi tải chunk, kể cả cái đã lọt
      // tới ErrorBoundary mà mình vừa cố tình giữ lại ở beforeSend. Đo thật 18/08: bỏ 4 chuỗi
      // chunk ra khỏi danh sách này mà event vẫn không bay đi, thủ phạm đúng là dòng này.
      /^(TypeError: )?Failed to fetch$/,
      // Circuit-breaker cố ý của inboxClient (auth-broken / no refresh token). Khi session
      // hết hạn thật, request đang in-flight reject với các lỗi này rồi app redirect /login —
      // benign by design, KHÔNG actionable. Tránh ngập dashboard. (GlitchTip issue 8)
      'auth_broken',
      'no_refresh_token',
      // 'zaloJSV2' = global do Zalo in-app browser (webview app Zalo) tự CHÈN vào trang khi
      // user mở link hub.cnvwork.com/signup TỪ TRONG app Zalo (rất phổ biến ở VN — share link
      // qua Zalo rồi bấm mở in-app). Script bridge của Zalo tham chiếu 'zaloJSV2' nhưng script
      // companion chưa init → ReferenceError ("Can't find variable: zaloJSV2" trên WebKit /
      // "zaloJSV2 is not defined" trên Chrome). KHÔNG phải code mình (grep 'zaloJSV2' toàn repo
      // = rỗng), KHÔNG ảnh hưởng luồng thật (signup OTP vẫn 200). Benign third-party injection,
      // cùng loại issue 9 (chrome-extension). Substring match phủ cả 2 wording browser.
      // (GlitchTip issue 12)
      'zaloJSV2',
    ],
    // Lỗi phát sinh TỪ browser extension (top frame của stack = extension URL) KHÔNG bao giờ
    // là bug của mình — extension monkeypatch window.fetch/DOM rồi throw. Drop toàn bộ.
    // (GlitchTip issue 9: chrome-extension .../frame_ant.js wrap fetch → 'Failed to fetch')
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
      /^safari-web-extension:\/\//,
      /^webkit-masked-url:/,
    ],
    beforeSend(event) {
      try {
        // Chunk-load: bỏ khi còn transient (lazyWithReload sẽ tự reload cho khỏi), GIỮ khi
        // đã rơi tới ErrorBoundary — tag do @cnv/ui/components/common/ErrorBoundary gắn.
        if (isChunkLoadEvent(event) && event.tags?.chunk_load !== 'true') return null;
        // Drop query string (có thể chứa token) + cookies/headers khỏi event.
        if (event.request?.url) event.request.url = event.request.url.split('?')[0];
        if (event.request) { delete event.request.cookies; delete event.request.headers; }
      } catch { /* never block */ }
      return event;
    },
  });

  // Cắm Sentry vào kernel cnv-ui. cnv-ui KHÔNG import @sentry/* (Core chưa có Sentry, import
  // thẳng là Core build fail) nên nó chỉ giữ cái móc — không gọi dòng này thì ErrorBoundary
  // dùng chung lại câm như trước 18/08. Xem @cnv/ui/lib/errorReporter.
  setErrorReporter((error, ctx) => Sentry.captureException(error, ctx));
}

export { Sentry };
